import React, { useEffect, useState } from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import {
  Send,
  Wallet,
  CheckCircle,
  QrCode,
  ChevronDown,
  Megaphone,
  Zap,
  X,
  Wifi,
  LayoutTemplate,
  ScrollText,
  MessageCircle,
} from "lucide-react";
import { Link } from "react-router-dom";
import TopImage from "../../images/laptop_img.webp";
import useAuthApi from "../../services/LandingPage/useAuthApi";
import useProfileDashboard from "../../services/Settings/useProfileDashboard";
import NoIndex from "../NoIndex";
import useDashboardAnalytics from "../../services/Dashboard/useDashboardAnalytics";
import useQRManager from "../../services/Dashboard/useQRManager";
import { toast } from "react-toastify";
import useBilling from "../../services/Billing/useBilling";
import { useNavigate } from "react-router-dom";
import useCampaigns from "../../services/Whatsapp/useCampaigns";

const areaData = [
  { day: "1 Day", approved: 20, rejected: 10 },
  { day: "5 Day", approved: 40, rejected: 15 },
  { day: "10 Day", approved: 120, rejected: 60 },
  { day: "15 day", approved: 150, rejected: 100 },
  { day: "20 day", approved: 35, rejected: 105 },
  { day: "25 day", approved: 155, rejected: 30 },
  { day: "30 day", approved: 195, rejected: 45 },
];

const StatBadge = ({ value, label, className }) => (
  <span
    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] sm:text-[11px] font-medium whitespace-nowrap ${className}`}
  >
    {value} {label}
  </span>
);

export default function Dashboard() {
  const [subscription, setSubscription] = useState(null);
  const { fetchSubscriptionDetails, loading } = useAuthApi();
  const { getWhatsappCredentials } = useProfileDashboard();
  const { getCampaigns } = useCampaigns();
  const [whatsappCred, setWhatsappCred] = useState(null);
  const [hasToken, setHasToken] = useState(false);
  const [haveCred, setHaveCred] = useState(false);
  const [credLoading, setCredLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const storedUser = JSON.parse(sessionStorage.getItem("user"));
  const [interval, setInterval] = useState(30);
  const [showDropdown, setShowDropdown] = useState(false);
  const userID = storedUser?.userid;
  const { addTopUp, topupLoading, qrCode } = useQRManager(userID);
  const [topUpModalOpen, setTopUpModalOpen] = useState(false);
  const [topUpUserId, setTopUpUserId] = useState("");
  const [amount, setAmount] = useState("");
  const { walletBalance } = useBilling(userID, interval);
  const navigate = useNavigate();
  const [campaignList, setCampaignList] = useState([]);
  const [campaignLoadingData, setCampaignLoadingData] = useState(false);
  const { campaignAnalytics, campaignLoading } = useDashboardAnalytics(
    userID,
    7,
    30,
    null,
    {},
    0,
  );

  const analytics = campaignAnalytics?.[0] || {};

  const {
    sent = analytics.sent || 0,
    delivered = analytics.delivered || 0,
    read = analytics.read || 0,
    failed = analytics.failed || 0,
  } = analytics;

  const COLORS = {
    sent: "#4F7DF3",
    delivered: "#22C55E",
    failed: "#EF4444",
    read: "#FF7A00",
  };

  const pieData = [
    { name: "Sent", value: sent, color: COLORS.sent },
    { name: "Delivered", value: delivered, color: COLORS.delivered },
    { name: "Failed", value: failed, color: COLORS.failed },
    { name: "Read", value: read, color: COLORS.read },
  ];

  const openTopUpModal = () => {
    setTopUpUserId(userID);
    setTopUpModalOpen(true);
  };

  const handleTopUp = async () => {
    if (!amount || Number(amount) <= 0) {
      toast.error("Please enter valid amount");
      return;
    }

    const success = await addTopUp({
      userId: userID,
      amount: amount,
      mobile: storedUser.mobile,
      name: storedUser.username,
    });

    if (success) {
      setTopUpModalOpen(false);
      setAmount("");
    }
  };

  // KpiCard Component

  const KpiCard = ({ type, value, onClick, qr, userName }) => {
    const config = {
      delivered: {
        title: "QR Scanner",
        footer: "Scan the QR to navigate",
        bg: "from-violet-300 via-purple-400 to-indigo-400",
        icon: <QrCode className="w-5 h-5 text-violet-600" />,
      },
      wallet: {
        title: "Wallet Balance",
        bg: "from-green-100 via-emerald-200 to-teal-300",
        icon: <Wallet className="w-5 h-5 text-green-600" />,
      },
      sent: {
        title: "Quick Action",
        bg: "from-sky-200 via-blue-300 to-blue-400",
        icon: <Zap className="w-5 h-5 text-sky-600" />,
      },
    };

    const item = config[type];
    if (!item) return null;

    return (
      <div
        className={`rounded-3xl p-5 text-white bg-gradient-to-br ${item.bg} flex flex-col min-h-[280px]`}
      >
        <div className="w-9 h-9 bg-white rounded-xl flex items-center justify-center">
          {item.icon}
        </div>

        {/* ── QR Card ── */}
   {type === "delivered" && (
  <>
    <p className="text-xs sm:text-sm font-semibold text-white mt-2 text-center sm:text-left">
      {item.title}
    </p>

    <div className="flex-1 rounded-xl sm:rounded-2xl my-2 sm:my-3 flex items-center justify-center p-2 sm:p-3 min-h-[180px] sm:min-h-[220px] md:min-h-[250px]">
      {qr ? (
        <img
          src={qr}
          alt="QR"
          className="w-full max-w-[150px] sm:max-w-[190px] md:max-w-[220px] aspect-square object-contain bg-white p-2 sm:p-3 rounded-xl sm:rounded-2xl"
        />
      ) : (
        <QrCode className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 opacity-30 text-white" />
      )}
    </div>

    <p className="text-[10px] sm:text-xs text-white/80 font-medium text-center leading-tight">
      {item.footer}
    </p>
  </>
)}

        {/* ── Wallet Card ── */}
        {type === "wallet" && (
          <>
            <p className="text-xs font-medium text-emerald-800/70 mt-3">
              Welcome back,
            </p>
            <p className="text-sm font-bold text-emerald-900 truncate">
              {userName || "Account"}
            </p>
            <p className="text-4xl font-bold text-emerald-900 tracking-tight mt-2 leading-none">
              {value}
            </p>
            <p className="text-sm font-medium text-emerald-800/80 mt-1 mb-3">
              Wallet Balance
            </p>

            {/* Credit Card */}
            <div className="flex-1 flex items-center justify-center mb-4">
              <div className="w-full max-w-[300px] h-[180px] rounded-2xl bg-gradient-to-br from-emerald-600 to-teal-700 p-4 flex flex-col justify-between shadow-lg relative overflow-hidden">
                {/* Shine circles */}
                <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -translate-y-8 translate-x-8" />
                <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/10 rounded-full translate-y-6 -translate-x-4" />

                {/* Chip + NFC */}
                <div className="flex items-center justify-between">
                  <div className="w-8 h-6 rounded-md bg-yellow-300/80 flex items-center justify-center">
                    <div className="w-5 h-4 rounded-sm border border-yellow-500/50 grid grid-cols-2 gap-[2px] p-[2px]">
                      <div className="bg-yellow-500/60 rounded-[1px]" />
                      <div className="bg-yellow-500/60 rounded-[1px]" />
                      <div className="bg-yellow-500/60 rounded-[1px]" />
                      <div className="bg-yellow-500/60 rounded-[1px]" />
                    </div>
                  </div>
                  <Wifi className="w-5 h-5 text-white/60 rotate-90" />
                </div>

                {/* Card number */}
                <p className="text-white/70 text-[10px] tracking-widest font-mono">
                  •••• •••• •••• 4242
                </p>

                {/* Name + Expiry */}
                <div className="flex items-center justify-between">
                  <p className="text-white text-[10px] font-semibold tracking-wide uppercase truncate max-w-[100px]">
                    {userName || "My Wallet"}
                  </p>
                  <p className="text-white/70 text-[9px] font-mono">12/28</p>
                </div>
              </div>
            </div>

            <button
              onClick={onClick}
              className="w-full bg-emerald-800 hover:bg-emerald-900 text-white rounded-2xl py-3 text-sm font-semibold transition-colors"
            >
              Add Top up
            </button>
          </>
        )}

        {/* ── Quick Action Card ── */}
        {type === "sent" && (
          <>
            <p className="text-sm text-sky-700 font-bold mt-2">{item.title}</p>
            <div className="grid grid-cols-2 md:grid-cols-1 lg:grid-cols-1 gap-3 flex-1 mt-3">
              <button
                onClick={() => navigate("/WhatsApp/Manage campaign")}
                className="bg-white rounded-2xl text-xs font-semibold text-sky-700 transition-colors flex flex-col items-center justify-center gap-1 py-2"
              >
                <Megaphone className="w-5 h-5 text-sky-700" />
                <span>Campaign</span>
                <span className="text-[10px] font-normal text-sky-900">
                  Send bulk msgs
                </span>
              </button>

              <button
                onClick={() => navigate("/WhatsApp/Template")}
                className="bg-white rounded-2xl text-xs font-semibold text-sky-700 transition-colors flex flex-col items-center justify-center gap-1 py-2"
              >
                <LayoutTemplate className="w-5 h-5 text-sky-700" />
                <span>Template</span>
                <span className="text-[10px] font-normal text-sky-900">
                  Manage templates
                </span>
              </button>

              <button
                onClick={() => navigate("/WhatsApp/Logs")}
                className="bg-white rounded-2xl text-xs font-semibold text-sky-700 transition-colors flex flex-col items-center justify-center gap-1 py-2"
              >
                <ScrollText className="w-5 h-5 text-sky-700" />
                <span>Logs</span>
                <span className="text-[10px] font-normal text-sky-900">
                  View activity
                </span>
              </button>

              <button
                onClick={() => navigate("/WhatsApp/Logs")}
                className="bg-white rounded-2xl text-xs font-semibold text-sky-700 transition-colors flex flex-col items-center justify-center gap-1 py-2"
              >
                <MessageCircle className="w-5 h-5 text-sky-700" />
                <span>Chat</span>
                <span className="text-[10px] font-normal text-sky-900">
                  Live support
                </span>
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  // Grid Layout
  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:col-span-6">
    <KpiCard type="delivered" qr={qrCode} />
    <KpiCard
      type="wallet"
      value={`₹ ${(Number(walletBalance) || 0).toFixed(2)}`}
      onClick={openTopUpModal}
    />
    <KpiCard type="sent" />
  </div>;

  useEffect(() => {
    const fetchCampaignData = async () => {
      if (!userID || !whatsappCred) return;

      try {
        setCampaignLoadingData(true);

        const phoneNumberId =
          whatsappCred?.phone_no_id || whatsappCred?.phoneNumberId || "";

        if (!phoneNumberId) {
          console.warn("phoneNumberId missing");
          return;
        }

        const data = await getCampaigns(phoneNumberId);

        setCampaignList(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
        setCampaignList([]);
      } finally {
        setCampaignLoadingData(false);
      }
    };

    fetchCampaignData();
  }, [userID, whatsappCred]);

  useEffect(() => {
    const getSubscription = async () => {
      const res = await fetchSubscriptionDetails(userID);

      if (res?.responseCode === "00") {
        setSubscription(res.data);
      }
    };

    if (userID) getSubscription();
  }, [userID]);

  useEffect(() => {
    if (loading || credLoading) return;

    const tempAlerts = [];
    const remainingDays = Number(subscription?.remaining_days || 0);

    // 1. SETUP
    if (!haveCred) {
      tempAlerts.push({
        type: "setup",
        message:
          "Please complete your one-time setup to start Isancharr Services.",
        button: "Complete Setup",
        route: "/Profile/Setup",
        color: "bg-red-100 text-red-700",
        btnColor: "bg-red-500",
      });
    }

    //  2. WHATSAPP ONBOARDING
    if (hasToken) {
      tempAlerts.push({
        type: "whatsapp",
        message:
          "Kindly complete the WhatsApp onboarding process to start using our WhatsApp service.",
        button: "Complete Onboarding",
        route: "/WhatsApp/Onboarding",
        color: "bg-blue-100 text-blue-700",
        btnColor: "bg-blue-500",
      });
    }

    //  3. UPGRADE PLAN
    if (remainingDays <= 0) {
      tempAlerts.push({
        type: "upgrade",
        message: "Your plan has expired. Please upgrade to continue services.",
        button: "Upgrade Plan",
        route: "/Profile/Plans",
        color: "bg-green-100 text-green-700",
        btnColor: "bg-green-600",
      });
    } else if (remainingDays <= 5) {
      tempAlerts.push({
        type: "upgrade",
        message: `Your subscription will expire in ${remainingDays} days. Please renew soon!`,
        button: "Upgrade Plan",
        route: "/Profile/Plans",
        color: "bg-green-100 text-green-700",
        btnColor: "bg-green-600",
      });
    }

    setAlerts(tempAlerts);
  }, [loading, credLoading, subscription, hasToken, haveCred]);

  const userData = JSON.parse(sessionStorage.getItem("user"));
  const userName =
    userData?.name || userData?.account_name || userData?.username || "Account";

  useEffect(() => {
    const fetchWhatsappCred = async () => {
      setCredLoading(true);

      const res = await getWhatsappCredentials(userID);

      if (res?.responseCode === "00") {
        setWhatsappCred(res.response);

        const token = res?.response?.token || res?.response?.access_token;

        if (!token) {
          setHasToken(true);
        } else {
          setHasToken(false);
        }

        setHaveCred(true);
      } else if (res?.responseCode === "01") {
        setHaveCred(false);
        setHasToken(true);
      }

      setCredLoading(false);
    };

    if (userID) fetchWhatsappCred();
  }, [userID]);

  return (
    <>
      <NoIndex />

      {/* ================= TOP BANNER ================= */}
      {alerts.map((alert, index) => (
        <div
          key={index}
          className={`w-full px-4 py-2 flex justify-between items-center text-sm  montserrat-font-1 ${alert.color}`}
        >
          <span className="flex-1 text-center">{alert.message}</span>

          <div className="flex items-center gap-3">
            <Link to={alert.route}>
              <button
                className={`text-white px-3 py-1 rounded ${alert.btnColor}`}
              >
                {alert.button}
              </button>
            </Link>

            {/*  Close button */}
            <button
              onClick={() =>
                setAlerts((prev) => prev.filter((_, i) => i !== index))
              }
              className="text-black font-bold"
            >
              <X size={20} />
            </button>
          </div>
        </div>
      ))}

      <div className=" text-gray-900">
        <div className=" p-3 sm:p-4">
          {/* Banner */}
          <div className="mb-6">
            <div className="flex flex-wrap justify-between rounded-2xl bg-gradient-to-r from-blue-500 to-teal-400 relative overflow-visible min-h-[160px]">
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute w-40 h-40 bg-white/10 rounded-full -top-10 -left-10 blur-2xl" />
                <div className="absolute w-56 h-56 bg-white/10 rounded-full -bottom-16 right-10 blur-2xl" />
              </div>

              {/* LEFT TEXT */}
              <div className="w-full md:w-7/12 p-5 relative z-10 mt-3">
                <div className="text-white text-lg md:text-3xl font-bold leading-snug montserrat-font-1">
                  Integrated Your System with Isancharr and Maximize All Benefits
                </div>

                <div className="text-white text-lg md:text-lg font-semibold mt-3 montserrat-font-2">
                  Connect your tools, launch campaign, and drive revenue
                  <br /> all from one intelligent workspace.
                </div>

                <div className="pt-4">
                  <Link to="/Profile/Plans">
                    <button className="bg-green-600 text-white font-semibold px-5 py-2 rounded-lg transition shadow montserrat-font-1">
                      Upgrade Plan
                    </button>
                  </Link>
                </div>
              </div>

              {/* IMAGE */}
              <div className="hidden md:flex w-4/12 justify-end items-end overflow-hidden relative z-10 mt-5 mr-5 mb-3">
                <img
                  src={TopImage}
                  alt=""
                  className="h-[200px] object-contain"
                />
              </div>
            </div>
          </div>

          {/* Top stats + overview */}
        
  {/* LEFT KPI */}
  <div className="mb-4 grid gap-4 grid-cols-1 xl:grid-cols-12">

  {/* LEFT KPI */}
  <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:col-span-6">
    <KpiCard type="delivered" qr={qrCode} />

    <KpiCard
      type="wallet"
      value={`₹ ${(Number(walletBalance) || 0).toFixed(2)}`}
      onClick={openTopUpModal}
      userName={userName}
    />

    <KpiCard type="sent" />
  </div>

  {/* RIGHT */}
  <div className="xl:col-span-6 rounded-md bg-white p-3 sm:p-4 md:p-5 shadow-sm ring-1 ring-black/5">

    {/* HEADER */}
    <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">

      <div>
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-md bg-blue-100 text-blue-600">
            <Megaphone size={18} className="sm:w-5 sm:h-5" />
          </div>

          <h3 className="text-base sm:text-lg md:text-xl font-semibold">
            Campaign Overview
          </h3>
        </div>

        <p className="text-xs sm:text-sm text-gray-500 ml-0 sm:ml-12">
          Live performance tracker
        </p>
      </div>

      {/* DROPDOWN */}
      <div className="relative w-full sm:w-auto">
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          className="flex w-full sm:w-auto items-center justify-center gap-2 rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-600"
        >
          {interval} Days <ChevronDown size={16} />
        </button>

        {showDropdown && (
          <div className="absolute right-0 mt-1 w-full sm:w-32 rounded-md border bg-white shadow-md z-10">
            {[7, 15, 30].map((val) => (
              <div
                key={val}
                onClick={() => {
                  setInterval(val);
                  setShowDropdown(false);
                }}
                className="cursor-pointer px-3 py-2 text-sm hover:bg-gray-100"
              >
                {val} Days
              </div>
            ))}
          </div>
        )}
      </div>
    </div>

    {/* BODY */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 items-center">

      {/* CHART */}
      <div className="flex justify-center">
        <div className="w-full max-w-[220px] sm:max-w-[260px] aspect-square">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={pieData}
                innerRadius={40}
                outerRadius={70}
                dataKey="value"
                startAngle={90}
                endAngle={-270}
                paddingAngle={2}
                stroke="none"
              >
                {pieData.map((entry) => (
                  <Cell key={entry.name} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* STATS */}
      <div className="space-y-2 sm:space-y-3">
        <div className="flex justify-between items-center rounded-lg px-3 sm:px-4 py-2 sm:py-3 bg-[#F0F9FD] border border-[#D6E4FF]">
          <span className="text-gray-800 text-xs sm:text-sm">Sent</span>
          <span className="text-base sm:text-xl font-semibold text-blue-500">
            {sent}
          </span>
        </div>

        <div className="flex justify-between items-center rounded-lg px-3 sm:px-4 py-2 sm:py-3 bg-[#D0FAE5] border border-[#C7F0D8]">
          <span className="text-gray-800 text-xs sm:text-sm">Delivered</span>
          <span className="text-base sm:text-xl font-semibold text-[#22C55E]">
            {delivered}
          </span>
        </div>

        <div className="flex justify-between items-center rounded-lg px-3 sm:px-4 py-2 sm:py-3 bg-[#FEE2E2] border border-[#FECACA]">
          <span className="text-gray-800 text-xs sm:text-sm">Failed</span>
          <span className="text-base sm:text-xl font-semibold text-[#EF4444]">
            {failed}
          </span>
        </div>

        <div className="flex justify-between items-center rounded-lg px-3 sm:px-4 py-2 sm:py-3 bg-[#FFEDD4] border border-[#FED7AA]">
          <span className="text-gray-800 text-xs sm:text-sm">Read</span>
          <span className="text-base sm:text-xl font-semibold text-[#FF7A00]">
            {read}
          </span>
        </div>
      </div>
    </div>
  </div>
</div>
</div>
</div>
        {/* Bottom section */}
        <div className="grid gap-4 lg:grid-cols-12 p-4">
          {/* Active Campaign */}
          <div className="rounded-md bg-white p-5 sm:p-4 shadow-sm ring-1 ring-black/5 lg:col-span-6">
            <div className="mb-2">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h3 className="text-lg sm:text-xl font-semibold">
                    Active Campaign
                  </h3>
                  <p className="text-xs text-gray-500">
                    Revenue & volume by channel
                  </p>
                </div>

                <Link to="/WhatsApp/Manage">
                  <button className="w-full rounded-md border border-gray-200 bg-blue-500 text-white px-3 py-2 text-xs sm:w-auto">
                    View Analytics
                  </button>
                </Link>
              </div>

              <div className="mt-2 -mx-3 sm:-mx-2 border-t border-gray-200/60" />
            </div>

            <div className="space-y-2">
              {campaignLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <div
                    key={i}
                    className="flex flex-col gap-3 border-b border-gray-100 pb-3"
                  >
                    <div className="shimmer h-4 w-2/3 rounded"></div>
                    <div className="flex gap-2">
                      <div className="shimmer h-4 w-10 rounded"></div>
                      <div className="shimmer h-4 w-12 rounded"></div>
                      <div className="shimmer h-4 w-10 rounded"></div>
                    </div>
                  </div>
                ))
              ) : campaignList.length === 0 ? (
                <div className="text-center text-gray-400 py-4">
                  No Campaign Found
                </div>
              ) : (
                campaignList.slice(0, 6).map((row, i) => (
                  <div
                    key={row.id || i}
                    className="flex flex-col gap-3 border-b border-gray-100 pb-3 last:border-b-0 lg:flex-row lg:items-center lg:justify-between lg:gap-8 lg:pb-2"
                  >
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-gray-700">
                        {row.campaign_name}
                      </div>
                      <div className="text-xs text-gray-400">
                        Type: {row.campaignType || "N/A"}
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-1 shrink-0">
                      <StatBadge
                        value={row.read || 0}
                        label="Read"
                        className="bg-[#ffefee] text-[#6E7DF3]"
                      />
                      <StatBadge
                        value={row.delivered || 0}
                        label="Delivered"
                        className="bg-[#EEF4FF] text-[#4F7DF3]"
                      />
                      <StatBadge
                        value={row.sent || 0}
                        label="Sent"
                        className="bg-[#ECFAF2] text-[#19A566]"
                      />
                      <StatBadge
                        value={row.failed || 0}
                        label="Failed"
                        className="bg-[#FDECEC] text-[#F24848]"
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Template Status */}
          <div className="rounded-md bg-white p-4 sm:p-4 shadow-sm ring-1 ring-black/5 lg:col-span-6">
            <div className="mb-2">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="text-lg font-semibold">Template Status</h3>
                  <p className="text-xs text-gray-500">
                    Revenue & volume by channel
                  </p>
                </div>

                <Link to="/WhatsApp/Template">
                  <button className="w-full rounded-md border border-gray-200 px-3 py-2 text-xs bg-blue-500 text-white sm:w-auto">
                    Create Template
                  </button>
                </Link>
              </div>

              <div className="mt-2 -mx-3 sm:-mx-2 border-t border-gray-200/60" />
            </div>

            <div className="h-[190px] sm:h-[210px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={areaData}>
                  <defs>
                    <linearGradient
                      id="approvedFill"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor="#7BE27A"
                        stopOpacity={0.55}
                      />
                      <stop
                        offset="95%"
                        stopColor="#7BE27A"
                        stopOpacity={0.04}
                      />
                    </linearGradient>
                    <linearGradient
                      id="rejectedFill"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor="#F39AA0"
                        stopOpacity={0.35}
                      />
                      <stop
                        offset="95%"
                        stopColor="#F39AA0"
                        stopOpacity={0.04}
                      />
                    </linearGradient>
                  </defs>

                  <CartesianGrid
                    stroke="#e5e7eb"
                    vertical={false}
                    horizontal={false}
                  />
                  <XAxis
                    dataKey="day"
                    tick={{ fontSize: 12, fill: "#8B8B8B" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: "#8B8B8B" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip />

                  <Area
                    type="monotone"
                    dataKey="approved"
                    stroke="#67D46D"
                    fill="url(#approvedFill)"
                    strokeWidth={1}
                  />
                  <Area
                    type="monotone"
                    dataKey="rejected"
                    stroke="#F08A92"
                    fill="url(#rejectedFill)"
                    strokeWidth={1}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="mt-2 grid gap-2 sm:grid-cols-3">
              <div className="rounded-xl bg-[#FFF7E8] px-3 py-2">
                <div className="text-sm font-semibold">350</div>
                <div className="mt-1 flex items-center gap-2 text-xs text-gray-600">
                  <span className="h-2 w-2 rounded-full bg-[#F3BC3C]" />
                  Pending
                </div>
              </div>

              <div className="rounded-xl bg-[#EDF9EA] px-3 py-2">
                <div className="text-sm font-semibold">100</div>
                <div className="mt-1 flex items-center gap-2 text-xs text-gray-600">
                  <span className="h-2 w-2 rounded-full bg-[#67D46D]" />
                  Approved
                </div>
              </div>

              <div className="rounded-xl bg-[#FDECEE] px-3 py-2">
                <div className="text-sm font-semibold">50</div>
                <div className="mt-1 flex items-center gap-2 text-xs text-gray-600">
                  <span className="h-2 w-2 rounded-full bg-[#F08A92]" />
                  Rejected
                </div>
              </div>
            </div>
          </div>
        </div>
      {/* </div> */}

      {topUpModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-xl bg-white shadow-lg mx-auto">
            {/* HEADER */}
            <div className="flex items-center justify-between border-b px-5 py-3">
              <h5 className="text-lg font-semibold">Topup Wallet</h5>
              <button
                onClick={() => {
                  setTopUpModalOpen(false);
                  setAmount("");
                }}
                className="text-gray-400 hover:text-gray-600 text-xl"
              >
                ✕
              </button>
            </div>

            <div className="px-5 py-4 space-y-4">
              <div>
                <p className="text-sm text-gray-500">Current Balance</p>
                <p className="text-green-600 font-bold text-lg">
                  ₹ {(Number(walletBalance) || 0).toFixed(2)}
                </p>
              </div>

              <div>
                <label className="text-sm text-gray-600">Name</label>
                <input
                  type="text"
                  value={storedUser.username || storedUser.mobile || "Customer"}
                  disabled
                  className="mt-1 w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="text-sm text-gray-600">Mobile</label>
                <input
                  type="text"
                  value={storedUser.mobile || ""}
                  disabled
                  className="mt-1 w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="text-sm text-gray-600">Enter Amount</label>
                <input
                  type="number"
                  placeholder="Enter amount"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t px-5 py-3">
              <button
                onClick={() => {
                  setTopUpModalOpen(false);
                  setAmount("");
                }}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-100"
              >
                Cancel
              </button>

              <button
                onClick={handleTopUp}
                disabled={loading}
                className="rounded-md bg-green-600 px-4 py-2 text-sm text-white hover:bg-green-700 disabled:opacity-50"
              >
                {loading ? "Processing..." : "Proceed"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
